import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import { businessService, BusinessListing } from '../services/businessService';
import { getTotalFranchiseCount } from '../services/franchiseService';
import { MAIN_CATEGORIES } from '../utils/categories';
import { CANADIAN_PROVINCES } from '../utils/locations';
import { getCachedTotalCount, setCachedTotalCount } from '../utils/cacheUtils';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [featuredBusinesses, setFeaturedBusinesses] = useState<BusinessListing[]>([]);
  const [totalBusinessCount, setTotalBusinessCount] = useState<number | null>(null);
  const [totalFranchiseCount, setTotalFranchiseCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedProvince, setSelectedProvince] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  // Redirect to /sell-business if payment success parameters are present
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const draftId = urlParams.get('draft');
    
    if (paymentStatus === 'success' && draftId) {
      // Redirect to sell-business page with the same parameters
      navigate(`/sell-business?payment=success&draft=${draftId}`, { replace: true });
    }
  }, [navigate]);

  // Use shared Canadian provinces list
  const provinces = CANADIAN_PROVINCES;

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (selectedProvince) {
      params.append('province', selectedProvince);
    }
    if (selectedCategory) {
      params.append('category', selectedCategory);
    }
    navigate(`/businesses?${params.toString()}`);
  };

  // Load featured businesses from Firestore
  useEffect(() => {
    const loadFeaturedBusinesses = async () => {
      try {
        setLoading(true);
        const businesses = await businessService.getFeaturedBusinesses(6);
        setFeaturedBusinesses(businesses);
      } catch (error) {
        console.error('Error loading featured businesses:', error);
      } finally {
        setLoading(false);
      }
    };

    loadFeaturedBusinesses();
  }, []);

  // Load total counts separately (optimized with caching and parallel queries)
  useEffect(() => {
    const cachedCount = getCachedTotalCount();
    if (cachedCount !== null) {
      setTotalBusinessCount(cachedCount);
    }

    const loadTotalCounts = async () => {
      try {
        const [totalCount, franchiseCount] = await Promise.all([
          businessService.getTotalActiveBusinessCount(),
          getTotalFranchiseCount().catch((err) => {
            console.warn('Error loading franchise count:', err);
            return 0;
          }),
        ]);
        setTotalBusinessCount(totalCount);
        setTotalFranchiseCount(franchiseCount);
        setCachedTotalCount(totalCount);
      } catch (error) {
        console.error('Error loading total business count:', error);
      }
    };

    loadTotalCounts();
  }, []);

  // SEO-optimized content
  const seoTitle = "BizBuyMax.com - Buy and Sell Businesses in North America | Business Opportunities";
  const seoDescription = "Find thousands of businesses for sale across North America. Browse restaurants, retail, services, franchises and more. Buy or sell your business with confidence. Established 1997.";
  const seoKeywords = "business for sale, buy business, sell business, franchise, business opportunity, Canada, Alberta, Ontario, BC, restaurants, retail, services";

  // Update page title on navigation
  usePageTitle(seoTitle);

  // Structured data for search engines
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "BizBuyMax.com",
    "url": "https://BizBuyMax.com",
    "description": "North America's leading business marketplace for buying and selling businesses, franchises, and business opportunities.",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://BizBuyMax.com/businesses?search={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{seoTitle}</title>
        <meta name="title" content={seoTitle} />
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content={seoKeywords} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://BizBuyMax.com" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:image" content="https://BizBuyMax.com/og-image.jpg" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://BizBuyMax.com" />
        <meta property="twitter:title" content={seoTitle} />
        <meta property="twitter:description" content={seoDescription} />
        <meta property="twitter:image" content="https://BizBuyMax.com/og-image.jpg" />
        
        {/* Additional SEO Meta Tags */}
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        <meta name="author" content="BizBuyMax.com" />
        <meta name="geo.region" content="CA" />
        <meta name="geo.placename" content="Canada" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://BizBuyMax.com" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      
      <main>
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-primary-600 to-primary-800 text-white py-12 sm:py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 leading-tight">
              {totalBusinessCount !== null 
                  ? `${totalBusinessCount.toLocaleString()} Businesses for Sale Right Now!`
                  : '... Businesses for Sale Right Now'}
              </h1>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6 leading-tight">
                  &#x21AA; Includes: {totalFranchiseCount !== null
                    ? `${totalFranchiseCount.toLocaleString()} Franchise Listings`
                    : '... Franchise Listings'}
              </h1>
              {/* Search Form */}
              <div className="max-w-4xl mx-auto px-4 mb-6 sm:mb-8">
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-center justify-center">
                  {/* Province Dropdown */}
                  <select
                    value={selectedProvince}
                    onChange={(e) => setSelectedProvince(e.target.value)}
                    className="w-full sm:w-auto sm:flex-1 px-4 py-3 rounded-md border border-white/30 bg-white text-gray-900 focus:ring-2 focus:ring-white focus:border-white transition-colors shadow-lg"
                  >
                    <option value="">All Provinces</option>
                    {provinces.map((province) => (
                      <option key={province} value={province}>
                        {province}
                      </option>
                    ))}
                  </select>
                  
                  {/* Category Dropdown */}
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full sm:w-auto sm:flex-1 px-4 py-3 rounded-md border border-white/30 bg-white text-gray-900 focus:ring-2 focus:ring-white focus:border-white transition-colors shadow-lg"
                  >
                    <option value="">All Categories</option>
                    {MAIN_CATEGORIES.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                  
                  {/* Search Button */}
                  <button
                    onClick={handleSearch}
                    className="w-full sm:w-auto bg-secondary-500 hover:bg-secondary-600 text-white px-6 sm:px-8 py-3 rounded-md font-medium transition-colors text-base sm:text-lg whitespace-nowrap"
                  >
                    Search Businesses
                  </button>
                </div>
              </div>
              
              {/* <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
                <Link
                  to="/businesses"
                  className="bg-secondary-500 hover:bg-secondary-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-md font-medium transition-colors text-base sm:text-lg w-full sm:w-auto"
                >
                  Browse Businesses
                </Link>
                <button className="bg-white hover:bg-gray-100 text-primary-600 px-6 sm:px-8 py-3 sm:py-4 rounded-md font-medium transition-colors text-base sm:text-lg w-full sm:w-auto">
                  Sell Your Business
                </button>
              </div> */}
            </div>
          </div>
        </section>

        {/* Why Sell with Us Section */}
        <section className="py-12 sm:py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
                Why Sellers Love Us?
              </h2>
            </div>
            
            {/* Logo Row */}
            <div className="flex items-center justify-center gap-4 sm:gap-6 mb-6 sm:mb-8">
              {/* BizBuyMax Logo */}
              <div className="flex items-center">
                <img 
                  src="/assets/images/bbm-logo.png" 
                  alt="BizBuyMax Logo" 
                  className="h-12 sm:h-16 md:h-20 w-auto"
                />
              </div>
              
              {/* Plus Sign */}
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-400">
                +
              </div>
              
              {/* REALTOR.ca Logo */}
              <div className="flex items-center">
                <img 
                  src="/assets/images/realtor-ca.png" 
                  alt="REALTOR.ca Logo" 
                  className="h-12 sm:h-16 md:h-20 w-auto"
                />
              </div>
            </div>
            
            {/* Description Text */}
            <div className="text-center">
              <p className="text-lg sm:text-xl text-gray-700 max-w-4xl mx-auto px-4">
                Every listing on BizBuyMax includes a free option to list your business on REALTOR.ca - the largest real estate platform in Canada! 
                No other platform in Canada can compare.
                <br />
                <br />
                No Commissions. No Hidden Costs.
              </p>
            </div>
            <br />
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
                <Link
                  to="/sell-business"
                  className="bg-primary-500 hover:bg-secondary-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-md font-medium transition-colors text-base sm:text-lg w-full sm:w-auto"
                >
                  Start Listing Your Business
                </Link>
              </div>
          </div>
        </section>

        {/* Stats Section */}
        {/* <section className="py-12 sm:py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary-600">
                {totalBusinessCount !== null 
                  ? `${totalBusinessCount.toLocaleString()} businesses for sale right now on BizBuyMax`
                  : '... businesses for sale right now on BizBuyMax'}
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8 text-center">
              <div>
                <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary-600 mb-1 sm:mb-2">25+</h3>
                <p className="text-sm sm:text-base text-gray-600">Years Experience</p>
              </div>
              <div>
                <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary-600 mb-1 sm:mb-2">8,800+</h3>
                <p className="text-sm sm:text-base text-gray-600">Successful Sales</p>
              </div>
              <div>
                <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary-600 mb-1 sm:mb-2">100%</h3>
                <p className="text-sm sm:text-base text-gray-600">Verified Listings</p>
              </div>
            </div>
          </div>
        </section> */}

        {/* Featured Businesses Section */}
        <section className="py-12 sm:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
                Featured Businesses
              </h2>
              <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
                Discover our hand-picked featured businesses with exceptional potential
              </p>
            </div>
            
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading featured businesses...</p>
              </div>
            ) : featuredBusinesses.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                {featuredBusinesses.map((business) => (
                  <div key={business.id} className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow overflow-hidden">
                    {/* Image Section */}
                    {business.media.images && business.media.images.length > 0 && business.media.images[0] && (
                      <Link to={business.basic.seoUrl ? `/business/${business.basic.seoUrl}` : `/business/${business.id}`} className="block group">
                        <div className="h-[250px] relative overflow-hidden bg-gray-100">
                          <img
                            src={business.media.images[0]}
                            alt={business.basic.title}
                            className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-110"
                            style={{ imageRendering: '-webkit-optimize-contrast' }}
                            loading="lazy"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.parentElement!.style.display = 'none';
                            }}
                          />
                          <div className="absolute top-2 left-2 flex gap-2">
                            <span className="bg-green-500 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-lg">
                              FEATURED
                            </span>
                            {business.basic.verified && (
                              <span className="bg-blue-500 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-lg">
                                VERIFIED
                              </span>
                            )}
                          </div>
                        </div>
                      </Link>
                    )}
                    
                    <div className="p-6">
                      {/* Badges if no image */}
                      {(!business.media.images || business.media.images.length === 0 || !business.media.images[0]) && (
                        <div className="flex items-center justify-between mb-3">
                          <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                            FEATURED
                          </span>
                          {business.basic.verified && (
                            <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                              VERIFIED
                            </span>
                          )}
                        </div>
                      )}
                      
                      <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                        <Link to={business.basic.seoUrl ? `/business/${business.basic.seoUrl}` : `/business/${business.id}`} className="hover:text-primary-600">
                          {business.basic.title}
                        </Link>
                      </h3>
                      
                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                        {business.basic.shortDescription}
                      </p>
                      
                      <div className="flex items-center text-sm text-gray-500 mb-3">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {business.location.city}, {business.location.province}
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="text-lg font-bold text-primary-600">
                          ${business.financial.askingPrice.toLocaleString()}
                        </div>
                        <div className="text-sm text-gray-500">
                          {business.basic.category}
                        </div>
                      </div>
                      
                      <div className="mt-4 flex flex-wrap gap-1">
                        {business.basic.tags.slice(0, 3).map((tag, index) => (
                          <span
                            key={index}
                            className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-600">No featured businesses available at the moment.</p>
              </div>
            )}
            
            {featuredBusinesses.length > 0 && (
              <div className="text-center mt-8">
                <Link
                  to="/businesses"
                  className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-md font-medium transition-colors"
                >
                  View All Businesses
                </Link>
              </div>
            )}
          </div>
        </section>
        
        {/* Features Section */}
        {/* <section className="py-12 sm:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
                Why Choose BizBuyMax.com?
              </h2>
              <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
                We provide the tools, resources, and support you need to successfully buy or sell a business.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Extensive Database</h3>
                <p className="text-gray-600">Search through our comprehensive database of businesses for sale across all industries and locations in Canada.</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Verified Listings</h3>
                <p className="text-gray-600">All our listings are verified and include detailed financial information to help you make informed decisions.</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Expert Support</h3>
                <p className="text-gray-600">Get expert guidance throughout your business buying or selling journey with our dedicated support team.</p>
              </div>
            </div>
          </div>
        </section> */}

        {/* Popular Categories Section */}
        <section className="py-12 sm:py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
                Popular Business Categories
              </h2>
              <p className="text-lg sm:text-xl text-gray-600 px-4">
                Explore businesses in high-demand categories
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
              {[
                { name: 'Restaurants', count: '342', icon: '🍽️' },
                { name: 'Retail', count: '289', icon: '🛍️' },
                { name: 'Services', count: '456', icon: '🔧' },
                { name: 'Manufacturing', count: '123', icon: '🏭' },
                { name: 'Technology', count: '89', icon: '💻' },
                { name: 'Healthcare', count: '156', icon: '🏥' },
                { name: 'Online Business', count: '167', icon: '🌐' }
              ].map((category) => (
                <Link
                  key={category.name}
                  to={`/businesses?category=${category.name.toLowerCase()}`}
                  className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow text-center"
                >
                  <div className="text-2xl sm:text-3xl mb-2">{category.icon}</div>
                  <h3 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">{category.name}</h3>
                  <p className="text-xs sm:text-sm text-gray-600">{category.count} listings</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 sm:py-16 bg-primary-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4">
              Ready to Start Your Business Journey?
            </h2>
            <p className="text-lg sm:text-xl mb-6 sm:mb-8 max-w-2xl mx-auto px-4">
              Join thousands of successful entrepreneurs who found their perfect business opportunity with us.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
              <Link
                to="/businesses"
                className="bg-white hover:bg-gray-100 text-primary-600 px-6 sm:px-8 py-3 rounded-md font-medium transition-colors text-base w-full sm:w-auto"
              >
                Start Browsing
              </Link>
              <button className="bg-secondary-500 hover:bg-secondary-600 text-white px-6 sm:px-8 py-3 rounded-md font-medium transition-colors text-base w-full sm:w-auto">
                List Your Business
              </button>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default HomePage; 